import type { WebshopProduct } from '@cldsystems/ws-api-client';
import { resolveCategoryIdForCompanyDiscount } from '@/utils/get-category-id';

export type PriceListProductPrice = {
  price: number;
  discountedPrice: number;
};

/**
 * Snapshot needed to mirror {@link FormattedPrice} / {@link useGetDiscountType} when
 * bucketing or filtering by “what the customer pays”.
 */
export type PriceFilterContext = {
  /** Category id → company category discount percent */
  categoryDiscountPercentByCategoryId: Record<string, number>;
  /** Product id → negotiated list row */
  priceListProductById: Record<string, PriceListProductPrice>;
};

export function buildPriceFilterContext(params: {
  companyCategoryDiscounts?: { id: string; discount: number }[];
  priceListProducts?: { id: string; price: number; discountedPrice: number }[];
}): PriceFilterContext {
  const categoryDiscountPercentByCategoryId: Record<string, number> = {};
  for (const row of params.companyCategoryDiscounts ?? []) {
    if (row.discount !== 0) {
      categoryDiscountPercentByCategoryId[row.id] = row.discount;
    }
  }

  const priceListProductById: Record<string, PriceListProductPrice> = {};
  for (const p of params.priceListProducts ?? []) {
    priceListProductById[p.id] = { price: p.price, discountedPrice: p.discountedPrice };
  }

  return { categoryDiscountPercentByCategoryId, priceListProductById };
}

/**
 * Effective unit price for filtering/sorting — same priority as {@link FormattedPrice} /
 * {@link useGetDiscountType}:
 * 1. Price list row for this product (negotiated price; optional promo on that row)
 * 2. Company category discount % on **catalog** `price`
 * 3. Catalog product discount (`discountedPrice` when below `price`)
 */
export function getEffectiveCustomerPrice(
  product: WebshopProduct,
  ctx?: PriceFilterContext | null,
): number {
  const catalogPrice = typeof product.price === 'number' ? product.price : 0;
  const catalogDiscounted =
    typeof product.discountedPrice === 'number' ? product.discountedPrice : catalogPrice;

  const priceListProductById = ctx?.priceListProductById ?? {};
  const categoryDiscountPercentByCategoryId = ctx?.categoryDiscountPercentByCategoryId ?? {};

  const listEntry = product.id != null ? priceListProductById[product.id] : undefined;
  if (listEntry) {
    const hasPromo = listEntry.discountedPrice > 0 && listEntry.discountedPrice < listEntry.price;
    return hasPromo ? listEntry.discountedPrice : listEntry.price;
  }

  const categoryId = resolveCategoryIdForCompanyDiscount(product);
  if (categoryId) {
    const pct = categoryDiscountPercentByCategoryId[categoryId];
    if (pct != null && pct !== 0) {
      return catalogPrice * (1 - pct / 100);
    }
  }

  if (catalogPrice > catalogDiscounted) {
    return catalogDiscounted;
  }

  return catalogPrice;
}

export function getMaxEffectiveCustomerPrice(
  products: WebshopProduct[],
  ctx?: PriceFilterContext | null,
): number {
  if (products.length === 0) return 0;
  return Math.max(...products.map(p => getEffectiveCustomerPrice(p, ctx)));
}
